import { Button, type ButtonProps } from "@/kit/ui/button";
import {
  ResponsiveModal,
  ResponsiveModalTrigger,
  ResponsiveModalClose,
  ResponsiveModalContent,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
  type ResponsiveModalProps,
} from "@/kit/ui/responsive-modal";
import { cn } from "@/utils/tailwind";
import { IconType } from "@/utils/types";
import { MouseEvent, ReactNode } from "react";

interface ConfirmDialogProps
  extends
    Pick<ButtonProps, "hue" | "variant" | "disabled">,
    ResponsiveModalProps {
  title: ReactNode;
  message: ReactNode;
  buttonMessage: ReactNode;
  buttonTitle?: string | undefined;
  onConfirm: (event: MouseEvent) => void;
  onCancel?: () => void;
  TriggerIcon?: IconType;
  ButtonIcon?: IconType | null;
  className?: string;
}

const ConfirmDialogTrigger = ({
  TriggerIcon,
  ButtonIcon,
  buttonMessage,
  buttonTitle,
  variant,
  hue,
  disabled,
}: ConfirmDialogProps) => {
  if (TriggerIcon) {
    return (
      <ResponsiveModalTrigger asChild>
        <Button variant="secondary" hue="grey" title={buttonTitle} icon>
          <TriggerIcon className="cursor-pointer" />
        </Button>
      </ResponsiveModalTrigger>
    );
  }
  return (
    <ResponsiveModalTrigger asChild>
      <Button
        disabled={disabled}
        variant={variant}
        hue={hue}
        className="flex items-center gap-2"
      >
        {buttonMessage}
        {ButtonIcon && <ButtonIcon />}
      </Button>
    </ResponsiveModalTrigger>
  );
};

const ConfirmDialogContent = ({
  title,
  message,
  buttonMessage,
  onConfirm,
  onCancel,
  variant,
  hue,
  disabled,
  children,
  className,
}: ConfirmDialogProps) => {
  return (
    <ResponsiveModalContent
      className={cn("max-h-[80vh] overflow-auto", className)}
    >
      <ResponsiveModalClose
        onClick={onCancel}
        className="absolute top-3 right-3"
      />
      <ResponsiveModalTitle className="p-4 text-center">
        {title}
      </ResponsiveModalTitle>
      <div className="flex flex-col w-full gap-4 px-4 py-4 md:py-2">
        <ResponsiveModalDescription>{message}</ResponsiveModalDescription>
        {children}
        <Button
          variant={variant}
          hue={hue}
          onClick={onConfirm}
          disabled={disabled}
          className="w-full"
        >
          {buttonMessage}
        </Button>
      </div>
    </ResponsiveModalContent>
  );
};

export const ConfirmDialog = (props: ConfirmDialogProps) => {
  return (
    <ResponsiveModal open={props.open} onOpenChange={props.onOpenChange}>
      <ConfirmDialogTrigger {...props} />
      <ConfirmDialogContent {...props} />
    </ResponsiveModal>
  );
};
